/*
 * Copyright (C) 2026 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

// Use '@canvas/i18next' (not bare 'i18next') so initI18next() has run.
import i18next from '@canvas/i18next'
import enFlat from 'translations/en.json'

const NAME_KEYS = [
  'date.day_names',
  'date.abbr_day_names',
  'date.month_names',
  'date.abbr_month_names',
]

// Only the two keys localizeNumber reads; the rest of number.format.* (numbers/booleans) is unused and must stay out of the i18next bundle.
const NUMBER_FORMAT_KEYS = ['number.format.delimiter', 'number.format.separator']

const CALENDAR_METADATA_KEYS = ['date.datepicker.column_headings', 'date.order']

// Locale-specific date/time join templates (I18n.t('#time.event') leftovers).
const JOIN_FORMAT_KEYS = ['time.event', 'timeValue.event']

export function extractDatetimeBundle(flat: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const key of Object.keys(flat)) {
    if (
      key.startsWith('date.formats.') ||
      key.startsWith('time.formats.') ||
      NAME_KEYS.includes(key) ||
      NUMBER_FORMAT_KEYS.includes(key) ||
      CALENDAR_METADATA_KEYS.includes(key) ||
      JOIN_FORMAT_KEYS.includes(key)
    ) {
      out[key] = flat[key]
    }
  }
  return out
}

// Keys are literal dotted strings (e.g. "date.formats.medium") -- only
// safe because ui/shared/i18next/index.ts sets keySeparator/nsSeparator
// to false.
export function registerDatetimeBundle(locale: string, flat: Record<string, unknown>): void {
  i18next.addResourceBundle(locale, 'datetime', extractDatetimeBundle(flat), true, true)
}

registerDatetimeBundle('en', enFlat as Record<string, unknown>)
